import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Sparkles, Loader2, ExternalLink, Trash2, Eye, EyeOff, MapPin } from "lucide-react";
import { toast } from "sonner";
import {
  adminListIndustryPages, adminGenerateIndustryPage, adminPatchIndustryPage, adminDeleteIndustryPage,
} from "@/lib/api";

export default function AdminIndustryPages() {
  const [pages, setPages] = useState([]);
  const [industry, setIndustry] = useState("");
  const [location, setLocation] = useState("");
  const [generating, setGenerating] = useState(false);

  const load = () => adminListIndustryPages().then(setPages).catch(() => toast.error("Failed to load pages"));
  useEffect(() => { load(); }, []);

  const generate = async () => {
    if (!industry.trim() || !location.trim()) { toast.error("Enter both industry and location"); return; }
    setGenerating(true);
    try {
      await adminGenerateIndustryPage(industry.trim(), location.trim());
      toast.success("Page generated");
      setIndustry(""); setLocation("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    }
    setGenerating(false);
  };

  const togglePublish = async (p) => {
    try { await adminPatchIndustryPage(p.id, { published: !p.published }); load(); }
    catch { toast.error("Update failed"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try { await adminDeleteIndustryPage(p.id); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <main className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/admin" className="text-slate-500 hover:text-brand text-sm flex items-center gap-1.5 mb-3"><ArrowLeft size={14} /> Back to console</Link>
        <h1 className="font-heading text-4xl font-extrabold tracking-tight text-slate-900">Industry × Location Pages</h1>
        <p className="text-slate-500 mt-1">AI-generate localized lead-generation pages served at clean SEO URLs.</p>

        <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-6 mt-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-xs font-semibold text-slate-500">Industry
              <input value={industry} onChange={(e) => setIndustry(e.target.value)} placeholder="e.g. Dentists" data-testid="industry-input"
                className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-800" />
            </label>
            <label className="text-xs font-semibold text-slate-500">Location
              <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Kochi" data-testid="location-input"
                onKeyDown={(e) => e.key === "Enter" && generate()}
                className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-800" />
            </label>
          </div>
          <button onClick={generate} disabled={generating} data-testid="industry-generate-btn"
            className="btn-primary rounded-xl px-5 py-3 mt-4 flex items-center gap-2 text-sm disabled:opacity-60">
            {generating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
            {generating ? "Generating…" : "Generate page"}
          </button>
          <p className="text-xs text-slate-400 mt-2">Uses AI. Regenerating an existing industry+location updates that page.</p>
        </div>

        <div className="mt-8 space-y-3">
          {pages.map((p) => (
            <div key={p.id} className="bg-white border border-slate-200 rounded-xl card-shadow p-4 flex items-center justify-between gap-4" data-testid={`admin-industry-${p.slug}`}>
              <div>
                <div className="flex items-center gap-1.5 text-xs font-semibold text-brand"><MapPin size={12} /> {p.location}</div>
                <div className="font-semibold text-slate-900">{p.title}</div>
                <div className="text-xs text-slate-400 mt-0.5">/industries/{p.slug} · {p.published ? <span className="text-emerald-600">Published</span> : <span className="text-amber-600">Hidden</span>}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <a href={`/industries/${p.slug}`} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-brand p-2" title="View"><ExternalLink size={16} /></a>
                <button onClick={() => togglePublish(p)} className="text-slate-400 hover:text-slate-700 p-2" title={p.published ? "Unpublish" : "Publish"} data-testid={`industry-toggle-${p.slug}`}>
                  {p.published ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>
                <button onClick={() => remove(p)} className="text-slate-300 hover:text-red-500 p-2" title="Delete" data-testid={`industry-delete-${p.slug}`}><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
          {pages.length === 0 && <p className="text-slate-400 text-sm">No pages yet — generate your first above.</p>}
        </div>
      </div>
    </main>
  );
}
